import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { getOrders } from '../../api/ordersService';
import { canViewCustomerDetails, CUSTOMER_DETAILS_PLACEHOLDER } from '../../utils/orderVisibility';
import { formatEventDateTime } from '../../utils/eventDate';
import { CustomButton, CustomTypography } from '../../components/shared';
import { ORDER_STATUS } from '../../utils/constants';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useVendorAuth } from '../../contexts/VendorAuthContext';

// S3 asset URLs
const EmptyOrdersIcon = 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/empty-orders-icon.svg';
const EmptyOrdersIllustration = 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/empty-orders-illustration.svg';

// Helper function to format date
const formatDate = (isoDate: string): string => {
  if (!isoDate) return '';
  
  const date = new Date(isoDate);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  const day = date.getDate();
  const month = months[date.getMonth()];
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${day} ${month}, ${hours}:${minutes}`;
};

// Helper function to format event date. Event dates are stored date-only, so
// this must not append a time - see utils/eventDate.ts.

// Helper function to format currency
const formatCurrency = (amount: number, currency: string = 'QAR'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

// Helper function to get first part of GUID
const getShortId = (id: string): string => {
  if (!id) return '';
  const parts = id.split('-');
  return parts[0] || id;
};

// Transform API order to component format
const transformOrder = (apiOrder: any): any => {
  try {
    // Handle both ApiOrder format and OrderRecord format
    const orderId = apiOrder.id?.toString() || apiOrder.orderId?.toString() || '';
    const productId = apiOrder.productId || '';
    const dateCreated = apiOrder.dateCreated || apiOrder.createdAt || '';
    // Try multiple sources for customer name
    const customerName = apiOrder.customer?.name 
      || apiOrder.customer?.contactName 
      || apiOrder.contactDetails?.contactName
      || (apiOrder.userId ? getShortId(apiOrder.userId) : '')
      || 'Unknown';
    const customerPhone = apiOrder.customer?.phone 
      || apiOrder.customer?.phoneNumber 
      || apiOrder.contactDetails?.phoneNumber
      || '';
    // Extract userId from order object (OrderRecord format) or customer object
    const userId = apiOrder.userId || apiOrder.customer?.userId || '';
    // Extract eventId
    const eventId = apiOrder.eventId || apiOrder.event?.eventId || '';
    // The vendor list endpoint returns raw OrderRecords, so the event lives on
    // `eventDetails`; the older nested `event` shape is kept as a fallback.
    // `eventDetails.eventDate` is date-only, while the order's `startDateTime`
    // is the real event start datetime (see vendorDeadlines.ts), so prefer it
    // whenever the order carries one.
    const eventDate = apiOrder.eventDetails?.eventDate || apiOrder.event?.date || apiOrder.event?.eventDate || '';
    const eventTime = apiOrder.startDateTime || apiOrder.event?.eventTime || '';
    const eventLocation = apiOrder.event?.location || apiOrder.event?.eventAddress || '';
    // Use product name if available, otherwise show shortened product ID
    const mainService = apiOrder.ordered?.mainService || apiOrder.productName || (productId ? getShortId(productId) : 'Unknown Service');
    const details = apiOrder.ordered?.details || '';
    const extraServices = apiOrder.ordered?.extraServices || 0;
    const total = apiOrder.total || apiOrder.amount || apiOrder.totalAmount || 0;
    const currency = apiOrder.currency || 'QAR';
    const status = apiOrder.status || 'Unknown';

    // Customer details (name, phone, address) unlock after vendor acceptance
    const customerVisible = canViewCustomerDetails(status);

    return {
      id: orderId,
      shortId: getShortId(orderId),
      productId: productId,
      date: formatDate(dateCreated),
      customer: customerVisible ? customerName : CUSTOMER_DETAILS_PLACEHOLDER,
      phone: customerVisible ? customerPhone : '',
      userId: userId, // Include userId for display
      eventId: eventId, // Include eventId for display
      eventDate: formatEventDateTime(eventTime || eventDate),
      location: customerVisible ? eventLocation : '',
      service: mainService,
      description: details,
      additionalServices: extraServices > 0 ? `+${extraServices} service${extraServices > 1 ? 's' : ''}` : '',
      total: formatCurrency(total, currency),
      status: status,
    };
  } catch (error) {
    console.error('Error transforming order:', error, apiOrder);
    return null;
  }
};

const Orders: React.FC = () => {
  usePageTitle('Orders');
  
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<string>('All');
  const [showEmptyState] = useState(false); // Toggle this to test empty state
  const navigate = useNavigate();
  const { vendor: sessionVendor } = useVendorAuth();

  // API state
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch orders from API
  useEffect(() => {
    const fetchOrders = async () => {
      if (!sessionVendor?.vendorId) {
        setError('Vendor session not found');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const response = await getOrders(sessionVendor.vendorId);
        
        if (response.success) {
          // Backend returns { success: true, data: { orders: OrderRecord[], count: number }, count: number }
          // Handle both array format and object format
          let ordersData: any[] = [];
          if (Array.isArray(response.data)) {
            ordersData = response.data;
          } else if (response.data && typeof response.data === 'object') {
            // Check if response.data has an 'orders' property
            const dataObj = response.data as any;
            if (Array.isArray(dataObj.orders)) {
              ordersData = dataObj.orders;
            } else if (Array.isArray(dataObj)) {
              ordersData = dataObj;
            }
          }
          
          // Transform orders, handling potential undefined values
          // Sort by createdAt descending (newest first) before transforming
          const sortedOrders = [...ordersData].sort((a, b) => {
            const dateA = typeof a.createdAt === 'number' ? a.createdAt : new Date(a.createdAt || 0).getTime();
            const dateB = typeof b.createdAt === 'number' ? b.createdAt : new Date(b.createdAt || 0).getTime();
            return dateB - dateA;
          });
          const transformedOrders = sortedOrders
            .filter(order => order != null) // Filter out null/undefined orders
            .map(transformOrder)
            .filter(order => order != null); // Filter out failed transformations
          
          setOrders(transformedOrders);
        } else {
          throw new Error('API returned unsuccessful response');
        }
      } catch (err) {
        console.error('Error fetching orders:', err);
        
        // Handle axios errors with better messages
        if (axios.isAxiosError(err)) {
          if (err.code === 'ERR_NETWORK' || err.code === 'ECONNREFUSED' || err.message.includes('Network Error')) {
            setError('Unable to connect to the server. Please check your connection and try again.');
          } else if (err.response) {
            setError(`Server error: ${err.response.status} - ${err.response.statusText}`);
          } else if (err.request) {
            setError('No response from server. Please try again later.');
          } else {
            setError(err.message);
          }
        } else {
          setError(err instanceof Error ? err.message : 'Failed to fetch orders');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [sessionVendor?.vendorId]);

  // Filter orders based on search term and active tab
  const filteredOrders = orders.filter(order => {
    // Search filter
    const matchesSearch = !searchTerm || 
      order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.service.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.location.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Status filter
    const matchesStatus = activeTab === 'All' || order.status === activeTab;
    
    return matchesSearch && matchesStatus;
  });

  // Calculate badge counts for tabs
  const pendingCount = orders.filter(order => order.status === ORDER_STATUS.PENDING).length;
  const changesCount = orders.filter(order => order.status === ORDER_STATUS.CHANGES).length;
  const confirmedCount = orders.filter(order => order.status === ORDER_STATUS.CONFIRMED).length;
  const inProgressCount = orders.filter(order => order.status === ORDER_STATUS.IN_PROGRESS).length;
  const deliveringCount = orders.filter(order => order.status === ORDER_STATUS.DELIVERING).length;
  const completedCount = orders.filter(order => order.status === ORDER_STATUS.COMPLETED).length;
  const cancelledCount = orders.filter(order => order.status === ORDER_STATUS.CANCELLED).length;

  const tabs = [
    { label: 'All', value: 'All', active: activeTab === 'All', badge: undefined },
    { label: ORDER_STATUS.PENDING, value: ORDER_STATUS.PENDING, active: activeTab === ORDER_STATUS.PENDING, badge: pendingCount > 0 ? pendingCount : undefined },
    { label: ORDER_STATUS.CHANGES, value: ORDER_STATUS.CHANGES, active: activeTab === ORDER_STATUS.CHANGES, badge: changesCount > 0 ? changesCount : undefined },
    { label: ORDER_STATUS.CONFIRMED, value: ORDER_STATUS.CONFIRMED, active: activeTab === ORDER_STATUS.CONFIRMED, badge: confirmedCount > 0 ? confirmedCount : undefined },
    { label: ORDER_STATUS.IN_PROGRESS, value: ORDER_STATUS.IN_PROGRESS, active: activeTab === ORDER_STATUS.IN_PROGRESS, badge: inProgressCount > 0 ? inProgressCount : undefined },
    { label: ORDER_STATUS.DELIVERING, value: ORDER_STATUS.DELIVERING, active: activeTab === ORDER_STATUS.DELIVERING, badge: deliveringCount > 0 ? deliveringCount : undefined },
    { label: ORDER_STATUS.COMPLETED, value: ORDER_STATUS.COMPLETED, active: activeTab === ORDER_STATUS.COMPLETED, badge: completedCount > 0 ? completedCount : undefined },
    { label: ORDER_STATUS.CANCELLED, value: ORDER_STATUS.CANCELLED, active: activeTab === ORDER_STATUS.CANCELLED, badge: cancelledCount > 0 ? cancelledCount : undefined },
  ];

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case ORDER_STATUS.PENDING.toLowerCase():
        return '#c5cbd3'; // Grey
      case 'approved':
        return '#426a53'; // Green (success)
      case ORDER_STATUS.CONFIRMED.toLowerCase():
        return '#426a53'; // Green (success)
      case ORDER_STATUS.CHANGES.toLowerCase():
      case 'changing':
        return '#f59e0b'; // Amber/Orange (warning)
      case 'draft':
        return '#9ca3af'; // Light grey
      case ORDER_STATUS.IN_PROGRESS.toLowerCase():
        return '#67314f'; // Purple/brown (brand color)
      case ORDER_STATUS.DELIVERING.toLowerCase():
        return '#67314f'; // Purple/brown (brand color)
      case ORDER_STATUS.COMPLETED.toLowerCase():
        return '#426a53'; // Green (success)
      case ORDER_STATUS.CANCELLED.toLowerCase():
        return '#925b64'; // Red (error)
      default:
        return '#c5cbd3'; // Default grey
    }
  };

  // Show loading state
  if (loading) {
    return (
      <Box
        sx={{
          height: '100vh',
          bgcolor: 'white',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress sx={{ color: '#67314f' }} />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box
        sx={{
          height: '100vh',
          bgcolor: 'white',
          width: '100%',
          px: 10,
          py: 5,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <Alert
          severity="error"
          sx={{
            borderRadius: '12px',
            fontFamily: 'inherit',
          }}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'white',
        width: '100%',
        px: 10,
        py: 5,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant="h1" 
              sx={{ 
                fontSize: '44px',
                fontWeight: 700,
                color: '#181818',
                lineHeight: 1.15,
                letterSpacing: '-0.44px',
                mb: 1
              }}
            >
              Orders
            </Typography>
            <Typography 
              sx={{ 
                fontSize: '16px',
                fontWeight: 400,
                color: '#181818',
                lineHeight: 1.4
              }}
            >
              Manage new service requests and handle placed orders
            </Typography>
          </Box>

          {/* Search */}
          <TextField
            placeholder="Search orders"
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            sx={{
              width: 280,
              '& .MuiOutlinedInput-root': {
                bgcolor: '#f6f8f8',
                borderRadius: 1.5,
                height: 56,
                '& fieldset': {
                  border: 'none',
                },
                '& input': {
                  fontSize: 16,
                  color: '#93989c',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#93989c' }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Primary Action Button */}
          <CustomButton
            variant="outlined"
            size="large"
            onClick={() => navigate('/settings')}
          >
            Set up availability...
          </CustomButton>
        </Box>

        {/* Tabs */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {tabs.map((tab, index) => (
            <Button
              key={index}
              onClick={() => setActiveTab(tab.value)}
              sx={{
                height: 39,
                px: 2.5,
                py: 1.25,
                borderRadius: 2,
                bgcolor: tab.active ? '#ac654d' : '#f6f8f8',
                color: tab.active ? 'white' : '#181818',
                fontSize: 16,
                fontWeight: 'normal',
                textTransform: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                '&:hover': {
                  bgcolor: tab.active ? '#ac654d' : '#f6f8f8',
                },
              }}
            >
              {tab.label}
              {tab.badge !== undefined && tab.badge > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '20px',
                    height: '20px',
                    padding: '0 6px',
                    borderRadius: '10px',
                    backgroundColor: tab.active ? 'rgba(255, 255, 255, 0.3)' : '#426A53',
                    color: '#FFFFFF',
                    fontSize: '12px',
                    fontWeight: 600,
                    lineHeight: '20px',
                  }}
                >
                  {tab.badge}
                </Box>
              )}
            </Button>
          ))}
        </Box>

        {/* Orders Table or Empty State */}
        {showEmptyState || filteredOrders.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              py: 5,
              px: 3,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Background Illustration */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 375,
                        height: 375,
                        zIndex: 0,
                      }}
                    >
              <Box
                component="img"
                src={EmptyOrdersIllustration}
                alt="Empty orders background"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>

            {/* Empty State Icon */}
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                width: 120,
                height: 120,
              }}
            >
              <Box
                component="img"
                src={EmptyOrdersIcon}
                alt="No orders"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>

            {/* Empty State Text */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                textAlign: 'center',
                position: 'relative',
                zIndex: 1,
              }}
            >
              <CustomTypography variant="h3" weight="bold">
                No orders
              </CustomTypography>
              <CustomTypography variant="body1">
                There are no new orders to show at the moment
              </CustomTypography>
            </Box>
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #f1f4f4' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ borderBottom: '1px solid #f1f4f4' }}>
                  <TableCell sx={{ py: 1.5, fontSize: 13, color: '#93989c', fontWeight: 'normal', width: 170 }}>
                    Customer
                  </TableCell>
                  <TableCell sx={{ py: 1.5, fontSize: 13, color: '#93989c', fontWeight: 'normal' }}>
                    Event
                  </TableCell>
                  <TableCell sx={{ py: 1.5, fontSize: 13, color: '#93989c', fontWeight: 'normal' }}>
                    Ordered
                  </TableCell>
                  <TableCell sx={{ py: 1.5, fontSize: 13, color: '#93989c', fontWeight: 'normal', width: 80 }}>
                    Total
                  </TableCell>
                  <TableCell sx={{ py: 1.5, width: 100 }}></TableCell>
                  <TableCell sx={{ py: 1.5, width: 56 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOrders.map((order) => (
                  <TableRow 
                    key={order.id} 
                    sx={{ 
                      borderBottom: '1px solid #f1f4f4',
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'rgba(103, 49, 79, 0.04)',
                      },
                    }}
                    onClick={() => navigate(`/orders/${order.id}`)}
                  >
                    <TableCell sx={{ py: 2, border: 'none' }}>
                      <Box>
                        <CustomTypography variant="body1">
                          {order.customer}
                        </CustomTypography>
                        <CustomTypography variant="caption">
                          {order.date}
                        </CustomTypography>
                        {order.userId && (
                          <CustomTypography variant="caption" sx={{ color: '#93989c' }}>
                            {getShortId(order.userId)}
                          </CustomTypography>
                        )}
                        <CustomTypography variant="caption">
                          {order.phone}
                        </CustomTypography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 2, border: 'none' }}>
                      <Box>
                        <CustomTypography variant="body1">
                          {order.eventDate}
                        </CustomTypography>
                        <CustomTypography variant="caption">
                          {order.location}
                        </CustomTypography>
                        {order.eventId && (
                          <CustomTypography variant="caption" sx={{ color: '#93989c', mt: 0.5 }}>
                            {getShortId(order.eventId)}
                          </CustomTypography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 2, border: 'none' }}>
                      <Box>
                        {order.productId ? (
                          <Link
                            to={`/products/${order.productId}`}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              textDecoration: 'none',
                              color: 'inherit',
                              display: 'inline',
                            }}
                          >
                            <CustomTypography 
                              variant="body1"
                              component="span"
                              sx={{
                                color: '#67314f',
                                '&:hover': {
                                  textDecoration: 'underline',
                                },
                                display: 'inline',
                              }}
                            >
                              {order.service}
                            </CustomTypography>
                          </Link>
                        ) : (
                          <CustomTypography variant="body1">
                            {order.service}
                          </CustomTypography>
                        )}
                        <CustomTypography variant="body2">
                          {order.description}
                        </CustomTypography>
                        {order.additionalServices && (
                          <CustomTypography variant="body2" sx={{ color: '#67314f' }}>
                            {order.additionalServices}
                          </CustomTypography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 2, border: 'none' }}>
                      <CustomTypography variant="body1">
                        {order.total}
                      </CustomTypography>
                    </TableCell>
                    <TableCell sx={{ py: 2, border: 'none' }}>
                      <Chip
                        label={order.status}
                        sx={{
                          bgcolor: getStatusColor(order.status),
                          color: 'white',
                          fontSize: 13,
                          fontWeight: 'normal',
                          height: 32,
                          borderRadius: 1,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2, border: 'none' }}>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/orders/${order.id}`);
                        }}
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: 2,
                          bgcolor: 'transparent',
                          '&:hover': {
                            bgcolor: 'rgba(103, 49, 79, 0.04)',
                          },
                        }}
                      >
                        <ArrowForwardIcon sx={{ color: '#67314f' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
  );
};

export default Orders;
